import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner, Container, Alert } from 'react-bootstrap';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import { locationFromUniqueId } from '../../utils/accessControl';

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const routeLocation = useLocation();
  const {
    userPermissions,
    loading: permissionsLoading,
    isPending,
    isSuspended,
    canAccessLocation,
  } = useUserPermissions();

  if (authLoading || permissionsLoading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Check if account is pending
  if (isPending) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">
          <Alert.Heading><><i className="bi bi-hourglass-split me-1"></i>Account Pending Approval</></Alert.Heading>
          <p>Your account is pending approval by an administrator.</p>
          <p>
            You will be able to access this page once your account has been
            activated.
          </p>
          <hr />
          <p className="mb-0">
            Your Email: <strong>{userPermissions?.email}</strong>
          </p>
        </Alert>
      </Container>
    );
  }

  // Check if account is suspended
  if (isSuspended) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading><><i className="bi bi-x-circle-fill me-1"></i>Account Suspended</></Alert.Heading>
          <p>Your account has been suspended.</p>
          <p>Please contact an administrator for more information.</p>
        </Alert>
      </Container>
    );
  }

  // Block access to specific records (update pages, summary) outside the user's allowed locations.
  // Record IDs are 10-digit numbers embedded in the URL path.
  const recordMatch = routeLocation.pathname.match(/\/(\d{10})(?:\/|$|\?)/);
  if (recordMatch && userPermissions) {
    const loc = locationFromUniqueId(recordMatch[1]);
    if (loc && !canAccessLocation(loc)) {
      return (
        <Container className="mt-5">
          <Alert variant="danger">
            <Alert.Heading><i className="bi bi-shield-x me-2"></i>Access Denied</Alert.Heading>
            <p>You do not have permission to access records in this location.</p>
            <Alert.Link href="/search">Go back to Search</Alert.Link>
          </Alert>
        </Container>
      );
    }
  }

  return children;
}

export default ProtectedRoute;
